import { TimelineMax, TweenMax, Power4 } from 'gsap';

class Carousel {
  constructor(index) {
    this.carousel = document.querySelectorAll('.js-carousel')[index];
    this.slides = [...this.carousel.querySelectorAll('.js-slide')];
    this.images = [...this.carousel.querySelectorAll('.js-image')];
    this.tags = [...this.carousel.querySelectorAll('.js-tag')];
    this.headings = [...this.carousel.querySelectorAll('.js-heading')];
    this.texts = [...this.carousel.querySelectorAll('.js-text')];
    this.buttons = [...this.carousel.querySelectorAll('.js-button')];
    this.bullets = [...this.carousel.querySelectorAll('.js-bullet')];
    this.prev = this.carousel.querySelector('.js-prev');
    this.next = this.carousel.querySelector('.js-next');

    this.data = {
      current: 0,
      next: 1,
      total: this.slides.length - 1,
      yPos: -30,
      xSpeed: 1,
      ySpeed: 0.4,
      yDelay: 0.35,
      xDelay: 1
    };
  }

  hideEl = (el) => {
    TweenMax.set(
      el,
      { autoAlpha: 0 }
    );
  }

  setStyles = () => {
    const currentBullet = this.bullets[this.data.current];

    // set active class to first bullet
    currentBullet.classList.add('carousel__bullet--active');

    [...this.slides].forEach((slide, index) => {
      if (index === 0) {
        TweenMax.set(slide, { autoAlpha: 1, zIndex: 2 });

        return;
      }

      // carousel w/ images
      if (this.carousel.getAttribute('data-type') === 'impression') {
        TweenMax.set(slide, { autoAlpha: 1, zIndex: 1, xPercent: -100 });
        TweenMax.set(this.images[index], { xPercent: 200 });
      }

      // carousel w/o images
      if (this.carousel.getAttribute('data-type') === 'post') {
        TweenMax.set(slide, { autoAlpha: 0, zIndex: 1 });

        this.hideEl(this.texts[index]);
      }

      // global
      if (this.tags.length >= 1) {
        this.hideEl(this.tags[index]);
      }

      this.hideEl(this.headings[index]);
      this.hideEl(this.buttons[index]);
    });
  }

  animateContent = (dir, tl, el, yPos, delay = 0) => {
    if (!tl || !el) return;

    tl
      .fromTo(
        el,
        this.data.ySpeed,
        { y: (dir === 'out' ? 0 : -yPos), autoAlpha: (dir === 'out' ? 1 : 0) },
        { y: (dir === 'out' ? yPos : 0), autoAlpha: (dir === 'out' ? 0 : 1), ease: Power4.easeOut },
        `-=${delay}`
      );
  }

  toggleSlideWithImage = (toggle, tl, el) => {
    tl.set(
      el,
      { zIndex: (toggle === 'hide' ? 1 : 2) }
    );
  }

  toggleSlide = (toggle, tl, el) => {
    tl.set(
      el,
      { zIndex: (toggle === 'hide' ? 1 : 2), autoAlpha: (toggle === 'hide' ? 0 : 1) }
    );
  }

  transition = (e) => {
    const dir = e.target.getAttribute('data-dir');
    const currentSlide = this.slides[this.data.current];
    const currentImage = this.images[this.data.current];
    const currentTag = this.tags[this.data.current];
    const currentHeading = this.headings[this.data.current];
    const currentText = this.texts[this.data.current];
    const currentButton = this.buttons[this.data.current];
    const currentBullet = this.bullets[this.data.current];
    let nextSlide;
    let nextImage;
    let nextTag;
    let nextHeading;
    let nextText;
    let nextButton;
    let nextBullet;

    let index;
    if (dir === '>') index = this.data.next;
    if (dir === '<') index = this.data.prev;

    nextSlide = this.slides[index];
    nextImage = this.images[index];
    nextTag = this.tags[index];
    nextHeading = this.headings[index];
    nextText = this.texts[index];
    nextButton = this.buttons[index];
    nextBullet = this.bullets[index];

    const tl = new TimelineMax({ paused: true });

    // animate out content
    this.animateContent('out', tl, currentTag, this.data.yPos, 0, (currentImage ? this.data.xDelay : 0));
    this.animateContent('out', tl, currentHeading, this.data.yPos, this.data.yDelay);
    this.animateContent('out', tl, currentText, this.data.yPos, this.data.yDelay);
    this.animateContent('out', tl, currentButton, this.data.yPos, this.data.yDelay);

    // animate out current bullet
    tl.call(() => currentBullet.classList.remove('carousel__bullet--active'));

    // show next slide
    if (nextSlide && nextImage) this.toggleSlideWithImage('show', tl, nextSlide);
    else this.toggleSlide('show', tl, nextSlide);

    // animate in current bullet
    tl.call(() => nextBullet.classList.add('carousel__bullet--active'));

    // hide current slide
    if (currentSlide && currentImage) this.toggleSlideWithImage('hide', tl, currentSlide);
    else this.toggleSlide('hide', tl, currentSlide);

    if (currentSlide && currentImage) {
      // animate container of next slide
      tl.fromTo(
        nextSlide,
        this.data.xSpeed,
        { xPercent: -100 },
        { xPercent: 0, ease: Power4.easeInOut },
        '-=0.5'
      );

      // animate image of next slide
      tl.fromTo(
        nextImage,
        this.data.xSpeed,
        { xPercent: 200 },
        { xPercent: 0, ease: Power4.easeInOut },
        `-=${this.data.xDelay}`
      );

      // reset container + image of current slide
      tl.set(currentSlide, { xPercent: -100 });
      tl.set(currentImage, { xPercent: 200 });
    }

    // animate in content
    this.animateContent('in', tl, nextTag, this.data.yPos, (currentImage ? 0.3 : 0));
    this.animateContent('in', tl, nextHeading, this.data.yPos, this.data.yDelay);
    this.animateContent('in', tl, nextText, this.data.yPos, this.data.yDelay);
    this.animateContent('in', tl, nextButton, this.data.yPos, this.data.yDelay);

    tl.play();

    // update data
    if (dir === '<') {
      this.data.next = this.data.current;
      this.data.current = this.data.current === 0 ? this.data.total : this.data.current - 1;
      this.data.prev = this.data.prev === 0 ? this.data.total : this.data.prev - 1;
    }

    if (dir === '>') {
      this.data.prev = this.data.current;
      this.data.current = this.data.current === this.data.total ? 0 : this.data.current + 1;
      this.data.next = this.data.next === this.data.total ? 0 : this.data.next + 1;
    }
  }

  listeners = () => {
    if (this.prev) this.prev.addEventListener('click', this.transition, { passive: true });
    if (this.next) this.next.addEventListener('click', this.transition, { passive: true });
  }

  init = () => {
    if (this.slides.length <= 1) return;

    this.setStyles();
    this.listeners();
  }

  destroy = () => {
    if (this.prev) this.prev.removeEventListener('click', this.transition);
    if (this.next) this.next.removeEventListener('click', this.transition);
  }
}

export default Carousel;
